// Cloud-backed service layer for Decyzon
import { supabase } from "@/integrations/supabase/client";
import type { TablesInsert } from "@/integrations/supabase/types";

// ─── Decisions ──────────────────────────────────────────────────────────────

export async function saveDecisionToCloud(params: {
  userId: string | null;
  rawQuery: string;
  parsedQuery: Record<string, unknown>;
  queryType: string;
  category: string;
  market: string;
  currency: string;
  priorityMode: string;
  confidenceLevel: string;
  recommendationType: string;
  winnerProductName: string;
  winnerPrice: string;
  winnerLabel: string;
  resultData: Record<string, unknown>;
  auditData: Record<string, unknown>;
}) {
  const row: TablesInsert<"decisions"> = {
    user_id: params.userId,
    raw_query: params.rawQuery,
    parsed_query: params.parsedQuery as TablesInsert<"decisions">["parsed_query"],
    query_type: params.queryType,
    category: params.category,
    market: params.market,
    currency: params.currency,
    priority_mode: params.priorityMode,
    confidence_level: params.confidenceLevel,
    recommendation_type: params.recommendationType,
    winner_product_name: params.winnerProductName,
    winner_price: params.winnerPrice,
    winner_label: params.winnerLabel,
    result_data: params.resultData as TablesInsert<"decisions">["result_data"],
    audit_data: params.auditData as TablesInsert<"decisions">["audit_data"],
  };

  const { data, error } = await supabase.from("decisions").insert(row).select("id").single();
  return { id: data?.id ?? null, error: error?.message ?? null };
}

export async function getDecisionHistory(userId: string) {
  const { data, error } = await supabase
    .from("decisions")
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: false })
    .limit(50);
  return { data: data ?? [], error: error?.message ?? null };
}

// ─── Watchlist ──────────────────────────────────────────────────────────────

export async function getCloudWatchlist(userId: string) {
  const { data, error } = await supabase
    .from("watchlist_items")
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: false });
  return { data: data ?? [], error: error?.message ?? null };
}

export async function addToCloudWatchlist(params: {
  userId: string;
  productName: string;
  query: string;
  category: string;
  label: string;
  price: string;
  priceNumeric: number;
  deliveryTime: string;
  confidence: string;
  coverage: string;
  watchType: string;
  decisionId?: string;
  notifyPriceDrop?: boolean;
}) {
  const row: TablesInsert<"watchlist_items"> = {
    user_id: params.userId,
    product_name: params.productName,
    query: params.query,
    category: params.category,
    label: params.label,
    price: params.price,
    price_numeric: params.priceNumeric,
    delivery_time: params.deliveryTime,
    confidence: params.confidence,
    coverage: params.coverage,
    watch_type: params.watchType,
    decision_id: params.decisionId ?? null,
    notify_price_drop: params.notifyPriceDrop ?? false,
  };

  const { data, error } = await supabase.from("watchlist_items").insert(row).select().single();
  return { data, error: error?.message ?? null };
}

export async function removeFromCloudWatchlist(id: string) {
  const { error } = await supabase.from("watchlist_items").delete().eq("id", id);
  return { error: error?.message ?? null };
}

// ─── Profile / Preferences ─────────────────────────────────────────────────

export async function getProfile(userId: string) {
  const { data, error } = await supabase
    .from("profiles")
    .select("*")
    .eq("user_id", userId)
    .single();
  return { data, error: error?.message ?? null };
}

export async function updateProfile(userId: string, updates: Partial<TablesInsert<"profiles">>) {
  const { error } = await supabase
    .from("profiles")
    .update(updates)
    .eq("user_id", userId);
  return { error: error?.message ?? null };
}

// ─── Events ─────────────────────────────────────────────────────────────────

export async function logEvent(params: {
  userId: string | null;
  decisionId?: string;
  eventType: string;
  metadata?: Record<string, unknown>;
}) {
  const row: TablesInsert<"decision_events"> = {
    user_id: params.userId,
    decision_id: params.decisionId ?? null,
    event_type: params.eventType,
    event_metadata: (params.metadata ?? {}) as TablesInsert<"decision_events">["event_metadata"],
  };
  await supabase.from("decision_events").insert(row);
}

// ─── Route Click Tracking ───────────────────────────────────────────────────

export async function logRouteClick(params: {
  userId: string | null;
  productName: string;
  sellerName: string;
  routeType: string;
  recommended: boolean;
  category: string;
  market: string;
  query: string;
}) {
  return logEvent({
    userId: params.userId,
    eventType: "route_clicked",
    metadata: {
      product_name: params.productName,
      seller_name: params.sellerName,
      route_type: params.routeType,
      recommended: params.recommended,
      category: params.category,
      market: params.market,
      query: params.query,
    },
  });
}

// ─── Watchlist Save from Decision Card ──────────────────────────────────────

export async function saveToCloudWatchlistFromCard(params: {
  userId: string;
  productName: string;
  query: string;
  category: string;
  label: string;
  price: string;
  priceNumeric: number;
  deliveryTime: string;
  confidence: string;
  coverage: string;
  type: "save" | "track" | "alert";
  notifyPriceDrop?: boolean;
}) {
  return addToCloudWatchlist({
    userId: params.userId,
    productName: params.productName,
    query: params.query,
    category: params.category,
    label: params.label,
    price: params.price,
    priceNumeric: params.priceNumeric,
    deliveryTime: params.deliveryTime,
    confidence: params.confidence,
    coverage: params.coverage,
    watchType: params.type,
    notifyPriceDrop: params.notifyPriceDrop,
  });
}

// ─── Alerts ─────────────────────────────────────────────────────────────────

export async function getAlerts(userId: string) {
  const { data } = await supabase
    .from("alerts")
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: false })
    .limit(20);
  return data ?? [];
}

export async function markAlertSeen(alertId: string) {
  await supabase.from("alerts").update({ seen_at: new Date().toISOString(), status: "seen" }).eq("id", alertId);
}

export async function createAlert(params: {
  userId: string;
  watchlistItemId?: string;
  alertType: string;
  message: string;
}) {
  const row: TablesInsert<"alerts"> = {
    user_id: params.userId,
    watchlist_item_id: params.watchlistItemId ?? null,
    alert_type: params.alertType,
    message: params.message,
  };
  const { data, error } = await supabase.from("alerts").insert(row).select().single();
  return { data, error: error?.message ?? null };
}

// ─── Stats ──────────────────────────────────────────────────────────────────

export async function getAccountStats(userId: string) {
  const [decisions, watchlist, alerts] = await Promise.all([
    supabase.from("decisions").select("id", { count: "exact", head: true }).eq("user_id", userId),
    supabase.from("watchlist_items").select("id", { count: "exact", head: true }).eq("user_id", userId),
    supabase.from("alerts").select("id", { count: "exact", head: true }).eq("user_id", userId).is("seen_at", null),
  ]);
  return {
    decisionsCount: decisions.count ?? 0,
    watchlistCount: watchlist.count ?? 0,
    unseenAlerts: alerts.count ?? 0,
  };
}

// ─── Unseen Alert Count ─────────────────────────────────────────────────

export async function getUnseenAlertCount(userId: string): Promise<number> {
  const { count } = await supabase
    .from("alerts")
    .select("id", { count: "exact", head: true })
    .eq("user_id", userId)
    .is("seen_at", null);
  return count ?? 0;
}

// ─── Price Alerts for Watchlist Items ───────────────────────────────────

export async function getAlertsForWatchlist(userId: string) {
  const { data } = await supabase
    .from("alerts")
    .select("*")
    .eq("user_id", userId)
    .eq("alert_type", "price_drop")
    .order("created_at", { ascending: false })
    .limit(50);
  return data ?? [];
}

// ─── Price Recheck (on-demand) ──────────────────────────────────────────

export async function recheckWatchlistPrices(): Promise<{ checked: number; alertsCreated: number }> {
  const { data, error } = await supabase.functions.invoke("recheck-prices");
  if (error) {
    console.error("[recheck] error:", error);
    return { checked: 0, alertsCreated: 0 };
  }
  return data ?? { checked: 0, alertsCreated: 0 };
}

// ─── Guest Migration ────────────────────────────────────────────────────────

// ─── Alert Preferences ──────────────────────────────────────────────────────

export async function getAlertPreferences(userId: string) {
  const { data } = await supabase
    .from("alert_preferences")
    .select("*")
    .eq("user_id", userId)
    .maybeSingle();
  return data;
}

// ─── Reorder Items ──────────────────────────────────────────────────────────

export async function getReorderItems(userId: string) {
  const { data } = await supabase
    .from("reorder_items")
    .select("*")
    .eq("user_id", userId)
    .eq("status", "active")
    .order("next_reorder_date", { ascending: true });
  return data ?? [];
}

export async function saveReorderItem(params: {
  userId: string;
  productName: string;
  category: string;
  cycleDays: number;
  lastPrice?: string;
  lastRetailer?: string;
  decisionId?: string;
}) {
  const nextDate = new Date();
  nextDate.setDate(nextDate.getDate() + params.cycleDays);

  const row = {
    user_id: params.userId,
    product_name: params.productName,
    category: params.category,
    cycle_days: params.cycleDays,
    last_price: params.lastPrice ?? null,
    last_retailer: params.lastRetailer ?? null,
    last_decision_id: params.decisionId ?? null,
    last_purchase_date: new Date().toISOString(),
    next_reorder_date: nextDate.toISOString(),
  };

  const { data, error } = await supabase.from("reorder_items").insert(row).select().single();
  return { data, error: error?.message ?? null };
}

// ─── Guest Migration ────────────────────────────────────────────────────────

export async function migrateGuestData(userId: string, localHistory: Array<Record<string, unknown>>, localWatchlist: Array<Record<string, unknown>>) {
  const results = { decisionsCreated: 0, watchlistCreated: 0, errors: [] as string[] };

  for (const entry of localHistory.slice(0, 20)) {
    const { error } = await saveDecisionToCloud({
      userId,
      rawQuery: (entry.query as string) ?? "",
      parsedQuery: {},
      queryType: (entry.category as string) ?? "",
      category: (entry.category as string) ?? "",
      market: (entry.country as string) ?? "",
      currency: "",
      priorityMode: "",
      confidenceLevel: (entry.confidence as string) ?? "",
      recommendationType: (entry.winnerLabel as string) ?? "",
      winnerProductName: (entry.winnerName as string) ?? "",
      winnerPrice: (entry.winnerPrice as string) ?? "",
      winnerLabel: (entry.winnerLabel as string) ?? "",
      resultData: {},
      auditData: {},
    });
    if (error) results.errors.push(error);
    else results.decisionsCreated++;
  }

  for (const item of localWatchlist.slice(0, 20)) {
    const { error } = await addToCloudWatchlist({
      userId,
      productName: (item.productName as string) ?? "",
      query: (item.query as string) ?? "",
      category: (item.category as string) ?? "",
      label: (item.label as string) ?? "",
      price: (item.price as string) ?? "",
      priceNumeric: Number(item.priceNumeric) || 0,
      deliveryTime: (item.deliveryTime as string) ?? "",
      confidence: (item.confidence as string) ?? "",
      coverage: (item.coverage as string) ?? "",
      watchType: (item.type as string) ?? "save",
    });
    if (error) results.errors.push(error);
    else results.watchlistCreated++;
  }

  return results;
}

// ─── Watchlist Intelligence ─────────────────────────────────────────────────

export async function getCloudWatchlistWithIntelligence(userId: string) {
  const { data, error } = await supabase
    .from("watchlist_items")
    .select("*, alerts!alerts_watchlist_item_id_fkey(id, message, alert_type, seen_at, created_at)")
    .eq("user_id", userId)
    .order("created_at", { ascending: false });
  return { data: data ?? [], error: error?.message ?? null };
}

// ─── Cloud Alerts for Homepage ──────────────────────────────────────────────

export async function getRecentUnseen(userId: string) {
  const { data } = await supabase
    .from("alerts")
    .select("*, watchlist_items!alerts_watchlist_item_id_fkey(product_name, query)")
    .eq("user_id", userId)
    .is("seen_at", null)
    .order("created_at", { ascending: false })
    .limit(5);
  return data ?? [];
}

// ─── Decision Aging ─────────────────────────────────────────────────────────

export async function getAgingDecisions(userId: string) {
  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
  const { data } = await supabase
    .from("decisions")
    .select("id, raw_query, category, winner_product_name, winner_price, created_at")
    .eq("user_id", userId)
    .lt("created_at", thirtyDaysAgo)
    .order("created_at", { ascending: false })
    .limit(3);
  return data ?? [];
}
